import axios from 'axios';
import http from '@/http';

// Has no 'api/v2' prefix
const generalApi = axios.create({
    baseURL: process.env.VUE_APP_API_URL,
});

// not authenticated
const api = axios.create({
    baseURL: process.env.VUE_APP_API_URL + 'api/v2',
    timeout: 0,
});

/** Be aware of whether 'api' or 'http' is being used. This is very important.
 *  Api is used for unauthenticated requests.
 *  http is for authenticated.
 *  TODO(Sounds like that should be swapped. As in, 'api' sounds like the var
 *      that should be used for authenticated routes, and 'http' the one for
 *      non (more general) routes, but maybe that's just me being a perfectionist.)
 */
export default {
    csrf() {
        return generalApi.get('/sanctum/csrf-cookie');
    },

    v2: {
        approval: {
            request(data) {
                return http.post('/approvals/email', data);
            },
        },
        auth: {
            email(data) {
                return api.post('/password/email', data);
            },
            login(data) {
                return api.post('/login', data);
            },
            logout() {
                return http.post('/logout'); // NOTE: using http!!!
            },
            register(data) {
                return api.post('/register', data);
            },
            token(token) {
                return api.get('/password/reset/' + encodeURI(token));
            },
            reset(data) {
                return api.post('/password/reset', data);
            },
            testVisit(data) {
                return api.post('/test-visit', data);
            },
        },
        cart: {
            index() {
                return http.get('/shopping-cart');
            },
            store(data) {
                return http.post('/shopping-cart', data);
            },
            clear() {
                return http.delete('/shopping-cart/clear/cart');
            },
        },
        facilities: {
            index() {
                return http.get('/facilities');
            },
            inmates(facilityId) {
                return http.get('/facilities/' + facilityId + '/inmates');
            },
            terminalSchedules(token, id) {
                return api.get(
                    '/facilities/token/' +
                        token +
                        '/terminals/' +
                        id +
                        '/schedules'
                );
            },
        },
        inmates: {
            availability(key, data) {
                return http.post('/inmates/' + key + '/availability', data);
            },
            dates(key, data) {
                return http.post('/inmates/' + key + '/dates', data);
            },
            intervals(key, data) {
                return http.post('/inmates/' + key + '/intervals', data);
            },
            times(key, data) {
                return http.post('/inmates/' + key + '/times', data);
            },
        },
        profile: {
            show() {
                return http.get('/profile');
            },
            preferredFacility(data) {
                return http.post('/profile/preferred-facility', data);
            },
            update(data) {
                return http.post('/profile', data);
            },
            exists(data) {
                return http.post('/profile/exists', data);
            },
        },
        verification: {
            verify(data) {
                return http.post('/verify-code', data);
            },
            resend() {
                return http.post('/verify-code/resend');
            },
        },
        visits: {
            anticipate(data) {
                return http.post('/visits', data);
            },
            cancel(id) {
                return http.put('/visits/' + id + '/cancel');
            },
            delete(id) {
                return http.delete('/visits/' + id);
            },
            index(url) {
                return http.get(url);
            },
            // 2022-03-28:  Covers scheduling routes if you are an invited visitor:
            invites(visitId, inviteId, data) {
                return http.post(
                    '/visits/' + visitId + '/invites/' + inviteId,
                    data
                );
            },

            // 2022-03-28:  Covers scheduling routes for primary visitor:
            //  1)  Normal Scheduling
            //  2)  Primary adding additional visitors
            //  3)  Scheduling when visits are in the cart.  NOTE that this route is HERE,
            //  //  and not in the 'cart' section
            schedule(url, data) {
                return http.post(url, data);
            },
            visitPage: {
                index(participantId, isTestVisit) {
                    return api.get(
                        '/visit-page/' +
                            (participantId + (isTestVisit ? '?test=true' : ''))
                    );
                },
                token(token, isTestVisit) {
                    return api.get(
                        '/visit-page-token/' +
                            (token + (isTestVisit ? '?test=true' : ''))
                    );
                },
                startRecording(visitId, sessionId) {
                    return api.post(
                        '/visit/' + visitId + '/openvidu/' + sessionId
                    );
                },
            },
        },
        visitor: {
            photoIds: {
                index(visitorId) {
                    return http.get('/visitors/' + visitorId + '/photo-ids');
                },
            },
            documents: {
                index(visitorId) {
                    return http.get(
                        '/visitors/' + visitorId + '/approved-documents'
                    );
                },
            },
        },
        vouchers: {
            confirm(waiver, data) {
                return http.put('/vouchers/' + waiver + '/confirm', data);
            },
        },
        photoId: {
            show(id) {
                return http.get('/photo-ids/' + id);
            },
        },
        document: {
            show(id) {
                return http.get('/approved-documents/' + id);
            },
        },
        support: {
            countries() {
                return http.get('/support/countries');
            },

            states() {
                return http.get('/support/states');
            },
        },
        testimonials() {
            return http.get('/testimonials');
        },
        visitTypes() {
            return http.get('/visit-types');
        },
    },

    // If you need unauthenticated axios instance, import api and use like api.api.etc.
    // If you need access to 'protected' axios instance, you can call window.http directly.
    api,
};
